import React from 'react';
import { CertQuestionCodeConstruction } from '@unity-learning/shared';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ContextCode } from './ContextCode';

interface QuestionCodeConstructionProps {
    question: Omit<CertQuestionCodeConstruction, 'validation'>;
    value: Record<string, string>;
    onChange: (value: Record<string, string>) => void;
    disabled?: boolean;
    /** Revue (page de résultats) : élément attendu par emplacement, ou liste des éléments acceptés.
     * Chaque emplacement prend la couleur juste ou fausse. */
    reviewAgainst?: Record<string, string | string[]>;
}

interface DragItem {
    id: string;
    type: string;
}

const ItemTypes = {
    BANK_ITEM: 'bankItem'
};

// Draggable bank item
const BankItem: React.FC<{
    id: string;
    value: string;
    disabled: boolean;
    isUsed: boolean;
}> = ({ id, value, disabled, isUsed }) => {
    const [{ isDragging }, drag] = useDrag(() => ({
        type: ItemTypes.BANK_ITEM,
        item: { id, type: ItemTypes.BANK_ITEM },
        canDrag: !disabled && !isUsed,
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        })
    }), [disabled, isUsed]);

    return (
        <div
            ref={drag}
            className={`
        p-3 rounded-lg font-mono text-sm border-2
        transition-all duration-200 select-none
        ${isUsed
                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-300 dark:border-gray-600 cursor-not-allowed opacity-50'
                    : isDragging
                        ? 'bg-blue-400 text-white border-blue-500 opacity-75 cursor-grabbing'
                        : 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700 cursor-grab hover:border-blue-400 dark:hover:border-blue-500'
                }
        ${disabled ? 'cursor-not-allowed' : ''}
      `}
        >
            <pre className="whitespace-pre-wrap text-xs">{value}</pre>
        </div>
    );
};

// Drop slot in code template
const DropSlot: React.FC<{
    slotId: string;
    droppedItemId: string | null;
    bankItems: { id: string; value: string }[];
    onDrop: (slotId: string, itemId: string) => void;
    onRemove: (slotId: string) => void;
    disabled: boolean;
    /** En revue seulement : l'emplacement contient-il un élément accepté ? */
    reviewRight?: boolean;
}> = ({ slotId, droppedItemId, bankItems, onDrop, onRemove, disabled, reviewRight }) => {
    const [{ isOver, canDrop }, drop] = useDrop(() => ({
        accept: ItemTypes.BANK_ITEM,
        drop: (item: DragItem) => {
            onDrop(slotId, item.id);
        },
        canDrop: () => !disabled,
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop()
        })
    }), [slotId, disabled, onDrop]);

    const droppedItem = bankItems.find(item => item.id === droppedItemId);
    const isReview = reviewRight !== undefined;

    return (
        <div
            ref={drop}
            className={`
        inline-block align-middle min-w-[12rem] max-w-full min-h-[36px] px-3 py-1.5 rounded border-2 border-dashed
        transition-all duration-200 my-0.5
        ${isReview
                    ? (reviewRight
                        ? 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-950'
                        : 'border-red-500 dark:border-red-400 bg-red-50 dark:bg-red-950')
                    : isOver && canDrop
                    ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/30'
                    : droppedItem
                        ? 'border-green-400 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-400 dark:border-gray-500 bg-gray-100 dark:bg-gray-800'
                }
      `}
            title={isReview ? (reviewRight ? 'Réponse juste' : 'Réponse fausse') : undefined}
        >
            {droppedItem ? (
                <div className="flex items-start justify-between gap-2">
                    <pre className={`font-mono text-xs whitespace-pre-wrap flex-1 ${isReview && !reviewRight
                        ? 'text-red-700 dark:text-red-200'
                        : 'text-green-700 dark:text-green-300'}`}>
                        {droppedItem.value}
                    </pre>
                    {!disabled && (
                        <button
                            type="button"
                            onClick={() => onRemove(slotId)}
                            title="Retirer"
                            aria-label="Retirer ce fragment"
                            className="text-red-500 hover:text-red-700 text-sm font-bold flex-shrink-0"
                        >
                            ✕
                        </button>
                    )}
                </div>
            ) : (
                <span className={`text-sm italic ${isReview ? 'text-red-700 dark:text-red-200' : 'text-gray-400'}`}>
                    {isReview ? 'Vide' : 'Déposez le code ici…'}
                </span>
            )}
        </div>
    );
};

// Main component
const QuestionCodeConstructionInner: React.FC<QuestionCodeConstructionProps> = ({
    question,
    value,
    onChange,
    disabled = false,
    reviewAgainst
}) => {
    const { content } = question;
    const { bank, code_template, context_code } = content;
    // Même règle que la notation serveur : un emplacement est juste s'il contient un des éléments acceptés
    const isSlotRight = (slotId: string): boolean | undefined => {
        if (!reviewAgainst) return undefined;
        const accepted = reviewAgainst[slotId];
        return (Array.isArray(accepted) ? accepted : [accepted]).includes(value[slotId]);
    };

    // Get list of used bank item IDs
    const usedItemIds = Object.values(value);

    const handleDrop = (slotId: string, itemId: string) => {
        // If the itemId is already placed somewhere else, remove it first
        const newValue = { ...value };
        for (const [sid, iid] of Object.entries(newValue)) {
            if (iid === itemId) {
                delete newValue[sid];
            }
        }
        newValue[slotId] = itemId;
        onChange(newValue);
    };

    const handleRemove = (slotId: string) => {
        const newValue = { ...value };
        delete newValue[slotId];
        onChange(newValue);
    };

    // Parse the code template and replace [[slot_id]] with drop slots
    const renderCodeWithSlots = () => {
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;

        // Match all [[slot_id]] patterns
        const regex = /\[\[(\w+)\]\]/g;
        let match;

        while ((match = regex.exec(code_template)) !== null) {
            // Add the text before this match
            if (match.index > lastIndex) {
                parts.push(
                    <span key={`text-${lastIndex}`} className="whitespace-pre">
                        {code_template.slice(lastIndex, match.index)}
                    </span>
                );
            }

            const slotId = match[1];
            parts.push(
                <DropSlot
                    key={`slot-${slotId}`}
                    slotId={slotId}
                    droppedItemId={value[slotId] || null}
                    bankItems={bank.map(b => ({ id: b.id, value: b.value }))}
                    onDrop={handleDrop}
                    onRemove={handleRemove}
                    disabled={disabled}
                    reviewRight={isSlotRight(slotId)}
                />
            );

            lastIndex = match.index + match[0].length;
        }

        // Add any remaining text
        if (lastIndex < code_template.length) {
            parts.push(
                <span key={`text-final`} className="whitespace-pre">
                    {code_template.slice(lastIndex)}
                </span>
            );
        }

        return parts;
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Context Code (if present) */}
            {context_code && (
                <div className="lg:col-span-3">
                    <ContextCode code={context_code} />
                </div>
            )}

            {/* Bank Column */}
            <div className="lg:col-span-1 space-y-2">
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">
                    Glissez les fragments de code :
                </div>
                <div className="space-y-2">
                    {bank.map((item) => (
                        <BankItem
                            key={item.id}
                            id={item.id}
                            value={item.value}
                            disabled={disabled}
                            isUsed={usedItemIds.includes(item.id)}
                        />
                    ))}
                </div>
            </div>

            {/* Editor Column */}
            <div className="lg:col-span-2">
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">
                    Construisez le code :
                </div>
                <div className="bg-gray-900 rounded-lg overflow-hidden">
                    <pre className="p-4 text-sm font-mono text-gray-100 leading-relaxed">
                        {renderCodeWithSlots()}
                    </pre>
                </div>
            </div>
        </div>
    );
};

// Wrapper with DnD Provider
export const QuestionCodeConstruction: React.FC<QuestionCodeConstructionProps> = (props) => {
    return (
        <DndProvider backend={HTML5Backend}>
            <QuestionCodeConstructionInner {...props} />
        </DndProvider>
    );
};

export default QuestionCodeConstruction;
